/**
 * Grant sahifasi (TZ §10.5).
 *
 * Tartib ariza CTR'ini maksimallashtirish uchun tanlangan:
 *   1 sarlavha  2 deadline  3 tez faktlar  4 bayroqchalar
 *   5 nima qoplanadi  6 kim ariza bera oladi  7 ARIZA (sticky)
 *   8 eslatma/saqlash/ulashish  9 o'xshashlar
 *
 * t("detail.apply") tugmasi Telegram MainButton — u ekran pastida DOIM
 * ko'rinib turadi va Mini App'ning tabiiy sticky elementi.
 *
 * Onboarding TALAB QILINMAYDI (T1): deep link bilan kelgan odam bu
 * sahifani darhol ko'radi.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api, getCached } from "../api";
import {
  CardList,
  DeadlineBadge,
  ErrorView,
  MapCard,
  Section,
  Spinner,
} from "../components";
import {
  flagLabel,
  formatLabel,
  fundingLabel,
  levelLabel,
  activeFlags,
  flagEmoji,
  formatDate,
} from "../format";
import { t } from "../i18n";
import {
  IconAlert,
  IconBookmark,
  IconBookmarkFilled,
  IconCheck,
  IconCoins,
  IconExternal,
  IconFiles,
  IconHeart,
  IconHeartFilled,
  IconLink,
  IconMapPin,
  IconShare,
  IconSparkle,
  IconText,
  IconUsers,
  TypeIcon,
} from "../icons";
import {
  alertUser,
  haptic,
  insideTelegram,
  openExternal,
  openTelegram,
  webApp,
} from "../telegram";
import type { OpportunityCard, OpportunityDetail } from "../types";

const SHARE_BASE = (import.meta.env.VITE_MINIAPP_BASE as string | undefined) ?? "";

/**
 * Ariza to'lovi qatori. Noma'lum bo'lsa `null` — qator umuman
 * ko'rsatilmaydi.
 *
 * UCH HOLAT ATAYLAB FARQLANADI va ularni birlashtirish MUMKIN EMAS:
 *
 *   summa ma'lum      -> «50 EUR»
 *   no_fee === true   -> «Bepul» (dalil bilan tasdiqlangan)
 *   no_fee === false  -> «Bor (summasi aniqlanmagan)»
 *   no_fee == null    -> null, ya'ni jimlik
 *
 * Oxirgi holat eng ko'p uchraydi. Uni «Bepul» deb ko'rsatish — odamni
 * xarajatini noto'g'ri rejalashtirib, ariza berish arafasida kutilmagan
 * to'lovga duch qilish demak. `=== true` tekshiruvi shuning uchun:
 * `undefined` va `null` ikkalasi ham «bepul» bo'lib qolmasin.
 */
function feeLabel(item: OpportunityDetail): string | null {
  if (item.application_fee && item.application_fee_currency) {
    return `${item.application_fee} ${item.application_fee_currency}`;
  }
  const noFee = item.flags?.no_fee;
  if (noFee === true) return t("detail.feeFree");
  if (noFee === false) return t("detail.feeUnknown");
  return null;
}

function BubbleButton({
  id,
  activeTooltip,
  setActiveTooltip,
  active,
  onClick,
  disabled,
  icon,
  label,
  tooltip,
  ariaLabel,
}: {
  id: string;
  activeTooltip: string | null;
  setActiveTooltip: (id: string | null) => void;
  active?: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  label?: React.ReactNode;
  tooltip: string;
  ariaLabel?: string;
}) {
  const timerRef = useRef<number | null>(null);
  const autoHideRef = useRef<number | null>(null);
  const isOpen = activeTooltip === id;

  const show = () => {
    setActiveTooltip(id);
    haptic("light");
    if (autoHideRef.current) clearTimeout(autoHideRef.current);
    autoHideRef.current = window.setTimeout(() => {
      setActiveTooltip(null);
    }, 2400);
  };

  const startPress = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      show();
    }, 220);
  };

  const endPress = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    show();
  };

  const handleMouseLeave = () => {
    endPress();
    setActiveTooltip(null);
  };

  // Komponent yo'qolganda ikkala taymer ham to'xtatiladi. Usiz
  // «bosib turish» yoki tooltip ochiq holatda boshqa sahifaga o'tilsa,
  // taymer keyinroq ishga tushib yo'q komponentning holatini
  // o'zgartirishga urinardi.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
    };
  }, []);

  return (
    <div
      className="btn-bubble-wrap"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={startPress}
      onTouchEnd={endPress}
      onTouchCancel={endPress}
    >
      <button
        className={`btn${active ? " btn-on" : ""}`}
        type="button"
        onClick={() => {
          endPress();
          onClick();
        }}
        disabled={disabled}
        aria-pressed={active}
        aria-label={ariaLabel}
      >
        {icon}
        {label ? <span>{label}</span> : null}
      </button>
      <div
        className={`glass-bubble ${isOpen ? "bubble-visible" : ""}`}
        role="tooltip"
      >
        {tooltip}
      </div>
    </div>
  );
}

export default function Detail({ id, source }: { id: number; source?: string }) {
  const [item, setItem] = useState<OpportunityDetail | null>(() => getCached<OpportunityDetail>(`detail:${id}`));
  const [similar, setSimilar] = useState<OpportunityCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  // Like uchun ALOHIDA bayroq: saqlash so'rovi ketayotganda yurak
  // tugmasi o'lik bo'lib qolmasin va aksincha. Ular mustaqil amallar.
  const [likeBusy, setLikeBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const detail = await api.detail(id, source);
      // Kanal postidagi 'Eslatma' tugmasidan kelganda avtomatik saqlash
      // va eslatmani yoqish
      let limitHit = false;
      if (source === "reminder" && !detail.reminder_enabled) {
        try {
          const res = await api.save(id, true);
          detail.saved = res.saved;
          detail.reminder_enabled = res.reminder_enabled;
          limitHit = res.limit_reached;
        } catch {
          // ignore
        }
      }
      setItem(detail);

      // SABABI AYTILADI. Server chegarani hurmat qiladi va holat to'g'ri
      // ko'rsatiladi, lekin foydalanuvchi «Eslatma o'chirilgan» yozuvini
      // ko'rib NEGA ekanini bilmasdi: u kanaldan aynan eslatma uchun
      // bosgan edi. Tushuntirishsiz bu ilova buzilgandek his qilinadi.
      // SON QOTIRILMAYDI (TZ §16.1). Chegara `config.FREE_REMINDER_LIMIT`
      // da va muhit o'zgaruvchisi bilan o'zgaradi — matnda aniq raqam
      // yozilsa, chegara o'zgargan kuni xabar yolg'on gapirib qoladi.
      // Hozir chegara amalda o'chirilgan, ya'ni bu yo'l ishlamaydi;
      // qaytarilsa esa matn baribir to'g'ri bo'lib qolaveradi.
      if (limitHit) {
        alertUser(
          t("detail.remindLimitSaved"),
        );
      }
      setSimilar(await api.similar(id).catch(() => []));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.loadFailed"));
    }
  }, [id, source]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.body.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    void load();
  }, [load]);

  // Ariza tugmasi — Telegram MainButton (ekran pastida doim ko'rinadi).
  //
  // `insideTelegram` SHART: Telegram SDK skripti oddiy brauzerda ham
  // yuklanadi va `webApp` obyektini beradi. `MainButton.show()` u yerda
  // xato bermaydi, `isVisible` ham `true` bo'ladi — LEKIN hech narsa
  // ko'rinmaydi. Natijada `webApp` bo'yicha tekshirsak, brauzerda
  // ariza tugmasi umuman yo'q bo'lib qolardi.
  useEffect(() => {
    const button = insideTelegram ? webApp?.MainButton : undefined;
    if (!button || !item) return;

    const target = item.apply_url ?? item.source_url;
    button.setText(item.apply_url ? t("detail.apply") : t("detail.sourceLink"));
    const onClick = () => {
      haptic("medium");
      void api.track({ kind: "apply_click", opportunity_id: item.id, source: "feed" });
      openExternal(target);
    };
    button.onClick(onClick);
    if (item.is_closed) button.disable();
    else button.enable();
    button.show();

    return () => {
      button.offClick(onClick);
      button.hide();
    };
  }, [item]);

  if (error) return <ErrorView message={error} onRetry={load} />;
  if (!item) return <Spinner label={t("common.loading")} />;

  const flags = activeFlags(item.flags);
  const facts: [string, string][] = [];
  if (item.degree_level.length) {
    facts.push([
      t("detail.level"),
      item.degree_level.map((l) => levelLabel(l)).join(", "),
    ]);
  }
  if (item.funding) facts.push([t("detail.funding"), fundingLabel(item.funding)]);
  if (item.format) facts.push([t("detail.format"), formatLabel(item.format)]);
  if (item.language_req) facts.push([t("detail.languageReq"), item.language_req]);
  const fee = feeLabel(item);
  if (fee) facts.push([t("detail.fee"), fee]);

  async function toggleSave() {
    if (!item || busy) return;
    setBusy(true);
    try {
      if (item.saved) {
        const result = await api.unsave(item.id);
        setItem({ ...item, saved: result.saved, reminder_enabled: false });
      } else {
        const result = await api.save(item.id, true);
        setItem({
          ...item,
          saved: result.saved,
          reminder_enabled: result.reminder_enabled,
        });
        if (result.limit_reached) {
          // Son qotirilmaydi — yuqoridagi izohga qarang.
          alertUser(
            t("detail.remindLimitShort"),
          );
        }
      }
      haptic("success");
    } catch {
      alertUser(t("detail.actionFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function toggleLike() {
    if (!item || likeBusy) return;
    setLikeBusy(true);

    // OPTIMISTIK: yurak darhol to'ladi, server javobini kutmaymiz.
    // Sekin mobil internetda javob 2 soniya kelishi mumkin va shu
    // vaqtda tugma «o'lik» his qilinadi — odam ikkinchi marta bosadi.
    const wasLiked = item.liked === true;
    const wasCount = item.like_count;
    setItem({ ...item, liked: !wasLiked });

    try {
      const result = wasLiked
        ? await api.unlike(item.id)
        : await api.like(item.id);
      // Server so'nggi so'z: son chegaradan past bo'lsa `null` qaytadi
      setItem((cur) =>
        cur ? { ...cur, liked: result.liked, like_count: result.like_count } : cur,
      );
      haptic(wasLiked ? "light" : "success");
    } catch {
      // Optimistik o'zgarishni QAYTARAMIZ — yolg'on holat qolmasin
      setItem((cur) =>
        cur ? { ...cur, liked: wasLiked, like_count: wasCount } : cur,
      );
      alertUser(t("detail.actionFailed"));
    } finally {
      setLikeBusy(false);
    }
  }

  function share() {
    if (!item) return;
    haptic("light");
    void api.track({ kind: "share", opportunity_id: item.id });
    if (!SHARE_BASE) {
      alertUser(t("detail.shareUnset"));
      return;
    }
    const link = `${SHARE_BASE}?startapp=o${item.id}_share`;
    openTelegram(
      `https://t.me/share/url?url=${encodeURIComponent(link)}` +
        `&text=${encodeURIComponent(item.title)}`,
    );
  }

  return (
    <div className="screen detail">
      {item.is_closed ? (
        <div className="banner banner-closed">
          <IconAlert size={17} />
          {t("detail.closedNotice")}
        </div>
      ) : null}

      <div className="detail-head">
        <span className="detail-icon">
          <TypeIcon type={item.type} size={22} />
        </span>
        {/*
          ZAXIRA BELGISI (MultiLang_TZ M5, §10.2).

          So'ralgan tilda tarjima yo'q va manba tilidagi matn
          ko'rsatilyapti. Buni JIMGINA qoldirib bo'lmaydi: odam matn
          nega boshqa tilda ekanini bilmasa, ilova buzilgan deb
          o'ylaydi. Bir sahifada bitta til — va u qaysi til ekani
          aytiladi.
        */}
        {item.text_is_fallback ? (
          <p className="lang-fallback-note">
            {t("lang.fallback", { lang: item.text_lang })}
          </p>
        ) : null}
        <h1 className="detail-title">{item.title}</h1>
      </div>
      <div className="detail-meta">
        {item.organization ? <span>{item.organization}</span> : null}
        {item.host_country ? (
          <span>
            {flagEmoji(item.host_country)} {item.host_country}
          </span>
        ) : null}
      </div>

      <div className="detail-deadline">
        <div>
          <span className="muted">{t("detail.applyDeadline")}</span>
          <b>{formatDate(item.deadline_at)}</b>
        </div>
        <DeadlineBadge days={item.days_left} />
      </div>

      {facts.length ? (
        <dl className="facts">
          {facts.map(([label, value]) => (
            <div key={label} className="fact">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {flags.length ? (
        <div className="chip-row">
          {flags.map((flag) => (
            <span key={flag} className="pill pill-good">
              <IconCheck size={13} />
              {flagLabel(flag)}
            </span>
          ))}
        </div>
      ) : null}

      {item.description_uz ? (
        <Section title={t("detail.summary")} icon={<IconText size={17} />}>
          <div className="prose">
            {item.description_uz
              .split(/\n+/)
              .filter((p) => p.trim().length > 0)
              .map((para, idx) => (
                <p key={idx}>{para.trim()}</p>
              ))}
          </div>
        </Section>
      ) : null}

      {item.benefits.length ? (
        <Section title={t("detail.covered")} icon={<IconCoins size={17} />}>
          <ul className="list">
            {item.benefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title={t("detail.who")} icon={<IconUsers size={17} />}>
        <ul className="list">
          {/*
            Cheklov bo'lmaganda "Hammaga ochiq" DEB YOZILMAYDI.
            Manbalarning ko'pchiligi fuqarolikni umuman sanamaydi, ya'ni
            bizda ma'lumot yo'q — "ochiq" deyish biz bilmaydigan da'vo
            bo'lardi (MultiLang_TZ §3.5). Shuning uchun "topilmadi".
          */}
          <li>
            {item.restriction ?? t("detail.noRestriction")}
          </li>
          {item.eligibility_evidence ? (
            <li className="muted-note">
              {t("detail.evidence", { text: item.eligibility_evidence })}
            </li>
          ) : null}
          {item.age_min !== null || item.age_max !== null ? (
            <li>
              {t("detail.ageRange", { min: item.age_min ?? "—", max: item.age_max ?? "—" })}
            </li>
          ) : null}
          {item.requires_enrollment === true ? (
            <li>{t("detail.enrollmentRequired")}</li>
          ) : null}
          {item.language_req ? <li>Til talabi: {item.language_req}</li> : null}
        </ul>
      </Section>

      {/*
        TZ §10.5, 7-punkt. Ariza berishdan OLDIN turadi: odam nima
        yig'ishini bilib, keyin havolaga o'tsin.
        Bo'sh bo'lsa bo'lim ko'rsatilmaydi — "hujjatlar: aniqlanmadi"
        degan qator foyda bermaydi, faqat sahifani uzaytiradi.
      */}
      {item.organization || item.host_country ? (
        <Section title={t("detail.location")} icon={<IconMapPin size={17} />}>
          <MapCard
            organization={item.organization}
            country={item.host_country}
          />
        </Section>
      ) : null}

      {(item.documents ?? []).length ? (
        <Section title={t("detail.documents")} icon={<IconFiles size={17} />}>
          <ul className="list">
            {(item.documents ?? []).map((doc) => (
              <li key={doc}>{doc}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      {(item.stories ?? []).length ? (
        <Section title={t("detail.winners")} icon={<IconSparkle size={17} />}>
          <div className="stories-list">
            {(item.stories ?? []).map((story) => (
              <div key={story.id} className="story-card">
                <div className="story-head">
                  <span className="story-author">{story.author_name}</span>
                  {story.year ? <span className="pill">{story.year}</span> : null}
                  {story.is_verified ? (
                    <span className="pill pill-good" title={t("detail.proofVerified")}>
                      <IconCheck size={13} /> {t("detail.verified")}
                    </span>
                  ) : (
                    <span className="pill" title={t("detail.userStory")}>
                      {t("detail.userStory")}
                    </span>
                  )}
                </div>
                {story.university || story.country ? (
                  <p className="story-sub muted small">
                    {[story.university, story.country].filter(Boolean).join(", ")}
                  </p>
                ) : null}
                {story.story ? (
                  <p className="story-text">«{story.story}»</p>
                ) : null}
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/*
        Telegram TASHQARISIDA (brauzer) MainButton ko'rinmaydi, shuning
        uchun oddiy tugma chiziladi. `webApp` emas, `insideTelegram` —
        sabab yuqoridagi effektda yozilgan.
      */}
      {!insideTelegram ? (
        <a
          className="btn btn-primary btn-block"
          href={item.apply_url ?? item.source_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            void api.track({
              kind: "apply_click",
              opportunity_id: item.id,
              source: "feed",
            });
          }}
        >
          <IconExternal size={17} />
          {item.apply_url ? t("detail.apply") : t("detail.sourceLink")}
        </a>
      ) : null}

      <div className="action-row">
        <BubbleButton
          id="save"
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          active={item.saved}
          onClick={toggleSave}
          disabled={busy}
          icon={item.saved ? <IconBookmarkFilled size={17} /> : <IconBookmark size={17} />}
          label={item.saved ? t("detail.savedState") : t("detail.save")}
          tooltip={
            item.saved
              ? (item.reminder_enabled
                  ? t("detail.remindOn")
                  : t("detail.savedNoRemind"))
              : t("detail.saveHint")
          }
          ariaLabel={item.saved ? t("detail.unsave") : t("detail.save")}
        />
        <BubbleButton
          id="like"
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          active={item.liked === true}
          onClick={toggleLike}
          disabled={likeBusy}
          icon={item.liked ? <IconHeartFilled size={17} /> : <IconHeart size={17} />}
          label={item.like_count != null ? item.like_count : t("detail.like")}
          tooltip={
            item.liked
              ? `Sizga yoqdi (${item.like_count ?? 1} kishi yoqtirdi)`
              : `E'longa layk bosish (${item.like_count ?? 0} ta layk)`
          }
          ariaLabel={item.liked ? t("detail.unlike") : t("detail.like")}
        />
        <BubbleButton
          id="share"
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          onClick={share}
          icon={<IconShare size={17} />}
          label={t("detail.share")}
          tooltip={t("detail.shareHint")}
          ariaLabel={t("detail.share")}
        />
      </div>

      {/*
        Manba HAVOLA bo'lishi SHART, oddiy matn emas.
        Sabab: yuqoridagi t("detail.who") bo'limi ko'pincha
        t("detail.noRestriction") deb
        yozadi (MultiLang_TZ §3.5). Tekshirishni so'rab, manbaga yo'l bermaslik — bo'shliq.
        Ikkinchi sabab: ajratishni LLM qiladi va u xato qilishi mumkin
        (TZ T6) — asl sahifa doim bir bosish narida turishi kerak.
      */}
      <p className="source-row">
        <a
          className="source-link"
          href={item.source_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Manba: {item.source_name ?? new URL(item.source_url).hostname}
          <IconExternal size={16} />
        </a>
      </p>

      {similar.length ? (
        <Section title={t("detail.similar")} icon={<IconLink size={17} />}>
          <CardList items={similar} source="similar" />
        </Section>
      ) : null}
    </div>
  );
}
